import { NavTreeNode } from "./types";
import { TempoElement } from "./tempoElement";
import pako from "pako";
import { NavTreeUpdates } from "@/components/Canvas/Storyboard/StaticIframe/utils/navTreeLookupUtils";

/**
 * Converts a NavTreeNode to a serializable format by removing circular references
 * and non-serializable properties
 */
function prepareNavTreeForSerialization(node: NavTreeNode): any {
  const serializable: any = {
    ...node,

    // Serialize in special way
    tempoElement: {
      codebaseId: node.tempoElement.codebaseId,
      storyboardId: node.tempoElement.storyboardId,
      uniquePath: node.tempoElement.uniquePath,
    },
    children: node.children.map((child) =>
      prepareNavTreeForSerialization(child),
    ),
  };

  // Remove parent reference
  delete serializable.parent;

  return serializable;
}

/**
 * Restores circular references and parent pointers in a deserialized nav tree
 */
function restoreNavTreeStructure(node: any, parent?: NavTreeNode): NavTreeNode {
  // Reconstruct the TempoElement instance
  const tempoElement = new TempoElement(
    node.tempoElement.codebaseId,
    node.tempoElement.storyboardId,
    node.tempoElement.uniquePath,
  );

  const restored: NavTreeNode = {
    ...node,
    tempoElement,
    parent,
    children: [],
  };

  // Restore children with parent references
  restored.children = (node.children || []).map((child: any) =>
    restoreNavTreeStructure(child, restored),
  );

  return restored;
}

/**
 * Compresses a navigation tree for storage
 */
export function compressNavTree(navTree: NavTreeNode | null): string | null {
  if (!navTree) return null;

  try {
    // Prepare the tree for serialization
    const serializable = prepareNavTreeForSerialization(navTree);

    // Convert to JSON string
    const jsonString = JSON.stringify(serializable);

    // Compress using pako
    const compressed = pako.deflate(jsonString);

    // Convert to base64 for storage - handle large arrays without stack overflow
    let binaryString = "";
    for (let i = 0; i < compressed.length; i++) {
      binaryString += String.fromCharCode(compressed[i]);
    }
    const base64 = btoa(binaryString);

    return base64;
  } catch (error) {
    console.error("Error compressing nav tree:", error);
    return null;
  }
}

/**
 * Decompresses a navigation tree from storage
 */
export function decompressNavTree(
  compressedData: string | null,
): NavTreeNode | null {
  if (!compressedData) return null;

  try {
    // Decode from base64
    const binaryString = atob(compressedData);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Decompress using pako
    const decompressed = pako.inflate(bytes, { to: "string" });

    // Parse JSON
    const parsed = JSON.parse(decompressed);

    // Restore the tree structure with parent references
    const restored = restoreNavTreeStructure(parsed);

    return restored;
  } catch (error) {
    console.error("Error decompressing nav tree:", error);
    return null;
  }
}

/**
 * Rebuilds lookups from a decompressed nav tree
 */
export function rebuildLookupsFromNavTree(
  navTree: NavTreeNode,
  storyboardId: string,
): NavTreeUpdates {
  const elementKeyToNavNodeLookup: { [elementKey: string]: NavTreeNode } = {};
  const codebaseIdToNavNodeLookup: { [codebaseId: string]: NavTreeNode[] } = {};
  const domUniquePathElementKeyLookup: {
    [domUniquePathElementKey: string]: NavTreeNode;
  } = {};
  const allCodebaseIds = new Set<string>();

  // Recursive function to traverse the tree and build lookups
  function traverseNode(node: NavTreeNode) {
    if (!node) {
      return;
    }

    // Need to do this conversion as all these functions are stripped from the object
    node.tempoElement = node.tempoElement
      ? TempoElement.fromOtherElement(node.tempoElement)
      : TempoElement.empty();

    const elementKey = node.tempoElement.getKey();

    // Add to element key lookup
    if (elementKey) {
      elementKeyToNavNodeLookup[elementKey] = node;
      allCodebaseIds.add(node.tempoElement.codebaseId);
    }

    // Add to codebase ID lookup
    if (node.tempoElement.codebaseId) {
      if (!codebaseIdToNavNodeLookup[node.tempoElement.codebaseId]) {
        codebaseIdToNavNodeLookup[node.tempoElement.codebaseId] = [];
      }
      codebaseIdToNavNodeLookup[node.tempoElement.codebaseId].push(node);
    }

    // Add to DOM unique path lookup
    if (node.domUniquePath) {
      const elementKey = new TempoElement(
        node.tempoElement.codebaseId,
        storyboardId,
        node.domUniquePath,
      );
      domUniquePathElementKeyLookup[elementKey.getKey()] = node;
    }

    // Traverse children
    node.children?.forEach(traverseNode);
  }

  traverseNode(navTree);

  return {
    elementKeyLookup: elementKeyToNavNodeLookup,
    codebaseIdLookup: codebaseIdToNavNodeLookup,
    domUniquePathElementKeyLookup,
    codebaseIds: allCodebaseIds,
    navTree,
  };
}

/**
 * Compresses HTML content for storage
 */
export function compressHtml(html: string | null): string | null {
  if (!html) return null;

  try {
    // Compress using pako
    const compressed = pako.deflate(html);

    // Convert to base64 for storage - handle large arrays without stack overflow
    let binaryString = "";
    for (let i = 0; i < compressed.length; i++) {
      binaryString += String.fromCharCode(compressed[i]);
    }
    const base64 = btoa(binaryString);

    // Add a prefix to indicate this is compressed data
    return `COMPRESSED:${base64}`;
  } catch (error) {
    console.error("Error compressing HTML:", error);
    // Return original HTML if compression fails
    return html;
  }
}

/**
 * Decompresses HTML content from storage
 * Handles both compressed and uncompressed HTML for backward compatibility
 */
export function decompressHtml(data: string | null): string | null {
  if (!data) return null;

  // Check if the data is compressed
  if (!data.startsWith("COMPRESSED:")) {
    // Return as-is if not compressed (backward compatibility)
    return data;
  }

  try {
    // Remove the prefix
    const base64 = data.substring("COMPRESSED:".length);

    // Decode from base64
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Decompress using pako
    const decompressed = pako.inflate(bytes, { to: "string" });

    return decompressed;
  } catch (error) {
    console.error("Error decompressing HTML:", error);
    // Return the original data if decompression fails
    return data;
  }
}
